/**
 * Demo bootstrap. Runs in place of src/index.tsx when Vite is invoked with
 * `--config vite.config.demo.ts` (via `pnpm start:demo` / `pnpm build:demo`).
 *
 * Order matters:
 *   1. Read demo token (URL → localStorage → null). No token → "invalid link" page.
 *   2. Resolve a profile: token → demo-profile endpoint. A missing/expired token (endpoint
 *      404s) clears the stored token and renders the "expired link" page.
 *   3. Start MSW with the composed handler set (profile drives which content blob is served).
 *   4. Mount React with AuthContextProvider primed and only MemberRoutes mounted.
 *
 * We deliberately skip Statsig/Datadog/Stripe/Segment/Amplitude — the demo bundle has those
 * aliased to stubs (Statsig) or simply never imports them.
 */
import 'utils/polyfills/promiseWithResolvers';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { clearStoredToken, fetchDemoProfile, readTokenFromUrlOrStorage } from './fetchDemoProfile';
import ExpiredPage from './ExpiredPage';
import DemoApp from './DemoApp';
import buildHandlers from './handlers';
import { DEMO_USER_PK } from './mockUser';
import { setDemoAnalyticsContext } from './demoTracking';

import { cleanupPersistedMemberData, sanitizeMemberData } from 'modules/common/utils/sanitizeMemberData';
import { Member } from 'modules/common/interfaces/member';
import theme from 'theme';

const container = document.getElementById('root');
if (!container) {
    throw new Error('Demo: #root element missing from demo.html');
}
const root = createRoot(container);

function renderWithTheme(node: React.ReactNode) {
    root.render(
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {node}
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

/**
 * Best-effort cache prefill. Fires the universal queries once, in parallel, via MSW and
 * seeds the React Query cache with the results — so when prod components mount, useMember /
 * useOAuth / etc. resolve synchronously from cache instead of waiting on a query roundtrip.
 *
 * Two side effects of doing this:
 *   1. First render of any tab feels noticeably snappier (no loading flash on the universal
 *      queries that every page mounts).
 *   2. We *don't* touch the prod query hooks — they still own their query keys; we just
 *      pre-populate the cache by key. Misalign a key and the prefill silently no-ops; the
 *      worst case is the demo behaves exactly like it did before this prefill existed.
 *
 * Add new entries here when you find a query that lights up "loading" on first nav and you
 * want to hide it.
 */
async function prefillQueryCache(queryClient: QueryClient) {
    const rawBase = (import.meta.env.VITE_DJANGO_URL as string | undefined) ?? 'http://localhost:8000/';
    const djangoBase = rawBase.replace(/\/?$/, '/');

    const fetchJson = async <T,>(path: string): Promise<T | undefined> => {
        try {
            const res = await fetch(djangoBase + path.replace(/^\//, ''));
            if (!res.ok) {
                return undefined;
            }
            return (await res.json()) as T;
        } catch {
            return undefined;
        }
    };

    const [members, oauth, payrollService, docTypes, btrStatus, itrStatus, itrList, zdSearch] = await Promise.all([
        fetchJson<Array<{ uuid: string }>>('/members/'),
        fetchJson<unknown>('/oauth/'),
        fetchJson<unknown>('/payroll/service/'),
        fetchJson<unknown>('/documents/document-types/'),
        fetchJson<unknown>('/btr/member/business_tax_return/status/?tax_year=2025'),
        fetchJson<unknown>('/itr/member/individual_tax_return/status/?tax_year=2025'),
        fetchJson<{ results: unknown[] }>('/itr/member/individual_tax_return/'),
        fetchJson<{ users: unknown[] }>('/zendesk/proxy/users/search.json?external_id=demo-member-uuid'),
    ]);

    const member = members?.[0];
    if (member) {
        queryClient.setQueryData(['member'], member);
        queryClient.setQueryData(['ZDUserId', member.uuid], zdSearch?.users?.[0]);
        // Seed usePersistData's cache too — useMenuAreas reads this synchronously on
        // first render (before the useMember `select` callback gets a chance to write),
        // so without this the sidebar hides everything with customValidation until a
        // second render lands. See modules/common/components/Layout/Sidebar/useMenuAreas.ts.
        try {
            localStorage.setItem(
                `${DEMO_USER_PK}.member`,
                JSON.stringify(sanitizeMemberData(member as unknown as Member))
            );
        } catch {
            // ignore quota / private-mode errors
        }
    }
    if (oauth) {
        queryClient.setQueryData(['oauth', 'useOAuth'], oauth);
        try {
            localStorage.setItem(`${DEMO_USER_PK}.oauth`, JSON.stringify(oauth));
        } catch {
            // ignore quota / private-mode errors
        }
    }
    if (payrollService) {
        queryClient.setQueryData(['payroll', 'usePayrollService'], payrollService);
    }
    if (docTypes) {
        queryClient.setQueryData(['documentTypes'], docTypes);
    }
    if (btrStatus) {
        queryClient.setQueryData(['business-tax-status', 2025], btrStatus);
    }
    if (itrStatus) {
        queryClient.setQueryData(['individual-tax-status', 2025], itrStatus);
    }
    if (itrList?.results) {
        // Mirror ITR_QUERY_KEYS.list({ year: 2025 }) = ['individual-tax-returns', 'list', { year: 2025 }]
        queryClient.setQueryData(['individual-tax-returns', 'list', { year: 2025 }], itrList.results);
    }
}

async function start() {
    // The demo's mock user pk is fixed across all three profiles, so usePersistData's
    // {pk}.member / {pk}.oauth localStorage cache leaks between sessions. Wipe it on every
    // boot so switching tokens (e.g. test-scorp → test-llc) doesn't carry over the previous
    // profile's membership_tier into useMenuAreas and leave Payroll wrongly visible.
    cleanupPersistedMemberData();

    const token = readTokenFromUrlOrStorage();
    if (!token) {
        renderWithTheme(<ExpiredPage variant="missing" />);
        return;
    }
    // Stamp the token before the fetch, so the expired-link page's events still say which link
    // it was. The token has to come from `readTokenFromUrlOrStorage` above: it strips
    // `demo_token` from the URL, so it cannot be re-read off `location` later.
    setDemoAnalyticsContext({ demoToken: token });

    const profile = await fetchDemoProfile(token);
    if (!profile) {
        clearStoredToken();
        renderWithTheme(<ExpiredPage variant="expired" />);
        return;
    }
    // Replace the token-only context with the full persona now that the profile resolved.
    setDemoAnalyticsContext({
        entityType: profile.entity_type,
        demoToken: token,
        leadUuid: profile.lead_uuid,
        anonymousId: profile.anonymous_id,
    });

    // MSW is browser-only — lazy-import so the static analysis path can never pull it server-side.
    const { setupWorker } = await import('msw/browser');
    const worker = setupWorker(...buildHandlers(profile));
    await worker.start({
        onUnhandledRequest(req, print) {
            const { url } = req;
            // Anything same-origin (SPA navigations, Vite modules, static assets) MUST bypass —
            // MSW's `warn`/`error` path re-fetches via passthrough and can fail with
            // "Failed to fetch" on navigation requests, blanking the page.
            if (url.startsWith(window.location.origin)) {
                return;
            }
            print.warning();
        },
        // Quiet by default — MSW otherwise logs every intercepted request which makes a normal
        // dashboard load look like a runaway loop. Flip via `?msw_debug=1` on the URL when needed.
        quiet: !new URLSearchParams(window.location.search).has('msw_debug'),
    });

    const queryClient = new QueryClient({
        defaultOptions: { queries: { retry: false, staleTime: Infinity } },
    });

    await prefillQueryCache(queryClient);

    renderWithTheme(
        <QueryClientProvider client={queryClient}>
            <DemoApp profile={profile} />
        </QueryClientProvider>
    );
}

start();
